import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DialogTypeEnum } from '@shared/components/dialog/enums/dialog-type.enum';
import { ImageViewerService } from '@core/services/image-viewer.service';
import { Observable, filter, take, combineLatest, map } from 'rxjs';
import { Store } from '@ngrx/store';
import {
	selectCart,
	selectCartForm,
	selectLanguaje,
	selectSuggestionItems,
} from '@store/selectors';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
	CART_UPDATE_FORM,
	CART_RESET_FORM,
	CART_FIND_BY_ID_SUCCESS,
	SUGGESTION_ITEMS_FIND,
	SUGGESTION_ITEMS_FIND_RESET,
	CART_PRODUCT_ORDER_PROCESS,
} from '@store/actions';
import {
	MenuItem,
	ModifierGroup,
	Price,
	Tax,
	ModifierDetail,
} from '@core/models';
import { CartService, I18nService } from '@core/services';
import { ComponentEnum } from '@core/enums';
import {
	HomeSingleProductOrderState,
	homeSingleProductResolveArticleRule,
	withDefaultSingleProductQuantity,
} from '@features/home/domain';
import {
	getPrimaryProductChannel,
} from '@features/home/util';
import { homeProcessor } from '@features/home/domain';
import { ISingleProductComponent, ISingleProductPresenter } from './interfaces';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { AppState } from '@store/reducers';
import { CartItemModifierProcessorService } from '@/features/shoppingcart/domain';

@Injectable({ providedIn: 'root' })
export class SingleproductPresenter
	extends IAbstractPresenter<ISingleProductComponent>
	implements ISingleProductPresenter
{
	translations: ILanguageObject;
	suggestionItems$: Observable<MenuItem[]>;
	formGroup: FormGroup;

	private detailsWithQuantity: ModifierDetail[] = [];
	private detailsWithQuantityNoAdd: ModifierDetail[] = [];
	private article$: Observable<MenuItem | null>;
	private articleForm$: Observable<MenuItem | null>;

	constructor(
		private modalCtrl: ModalController,
		private formBuilder: FormBuilder,
		private i18nService: I18nService,
		private store: Store<AppState>,
		private imageViewerService: ImageViewerService,
		private cartItemModifierProcessorService: CartItemModifierProcessorService,
		private cartService: CartService
	) {
		super();

		this.articleForm$ = this.store.select(selectCartForm);
		this.translations = this.i18nService.getTranslations(ComponentEnum.SingleProduct);
		this.suggestionItems$ = this.store.select(selectSuggestionItems).pipe(takeUntil(this.destroy$));
		this.article$ = combineLatest([
			this.store.select(selectCart),
			this.cartService.getStoreForStore(),
			]).pipe(
			map(([menuItem, cartList]) => {
				if (!menuItem) return null;

				return homeSingleProductResolveArticleRule({
					selectedArticle: menuItem,
					cartItems: cartList
				} as HomeSingleProductOrderState).selectedArticle;
			})
		);

		this.formGroup = this.formBuilder.group({
			idCartItem: [null],
			idArticulo: [null],
			cantItemsInCart: [0],
			suggestItems: this.formBuilder.array([]),
			gruposModificadores: this.formBuilder.array([]),
			impuestos: this.formBuilder.array([]),
			cantidad: [1],
			nota: [null],
			selectedPriceIndex: [0],
			precio: [0],
			precioBase: [0],
			productosCanal: this.formBuilder.group({
				idProducto: [null],
				nombre: [null],
				descripcion: [null],
				infoNutricional: [null],
				mostrarInstrucciones: [false],
				porPeso: [false],
				habilitado: [false],
				precioPersonalizado: [false],
				nombrePersonalizado: [false],
				precios: this.formBuilder.array([]),
				imagen: [null],
				localCanal: [null],
				mealSelected: [null],
			}),
		});

		this.formGroup.valueChanges
			.pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
			.subscribe(value => {
				this.store.dispatch(
					CART_UPDATE_FORM({
						cartForm: { ...value, productosCanal: [value.productosCanal] },
					})
				);
			}
		);
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		this.formGroup.get('selectedPriceIndex')?.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(index => {
				const preciosArray = this.formGroup.get('productosCanal.precios') as FormArray;
				const selectedPriceControl = preciosArray.at(index);
				if (selectedPriceControl) {
					this.formGroup.get('precio')?.patchValue(selectedPriceControl.value.precio);
				}
			});

		this.store.select(selectLanguaje).pipe(takeUntil(this.destroy$)).subscribe(lang => {
			this.view.translations = this.i18nService.getTranslations(ComponentEnum.SingleProduct, lang);
		});

		// TO DO Precision para tiendas de argentina
		// this.store
		// 	.select(selectStore)
		// 	.pipe(takeUntil(this.destroy$))
		// 	.subscribe(store => {
		// 		this.view.decimalPrecision = store?.isArgentina == true ? '1.0-2' : '1.2-2';
		// 	});

			this.article$.pipe(
				filter((article): article is MenuItem => article !== null),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe((article) => {
				this.initializePricesFormGroup(article);
				this.initializeModifierGroupsFormGroup(article);
				this.initializeTaxsFormGroup(article);
				this.initializeSuggestItemsFormGroup(article);

				const formValue = homeProcessor.processSingleProductForm({ article });
				this.formGroup.patchValue(formValue);
			});

		this.articleForm$
			.pipe(
				filter((articleForm): articleForm is MenuItem => !!articleForm),
				takeUntil(this.destroy$)
			)
			.subscribe(articleForm => {
				this.detailsWithQuantity = this.cartItemModifierProcessorService.calculateMenuItemModifiers(articleForm);
				this.detailsWithQuantityNoAdd = this.cartItemModifierProcessorService.calculateMenuItemModifiers(articleForm, false);

				if (articleForm.suggestItems && articleForm.suggestItems.length > 0) {
					this.store.dispatch(SUGGESTION_ITEMS_FIND({ ids: articleForm.suggestItems }));
				}
			}
		);
	}

	private initializePricesFormGroup(article: MenuItem): void {
		const preciosArray = this.formGroup.get('productosCanal.precios') as FormArray;
		preciosArray.clear();

		const prices = getPrimaryProductChannel(article)?.precios;
		if (prices) {
			prices.forEach((precio: Price) => {
				const priceGroup = this.formBuilder.group({
					nombre: [precio.nombre],
					precioBase: [precio.precioBase],
					idPrecio: [precio.idPrecio],
					precio: [precio.precio],
					specialPriceAmount: [precio.specialPriceAmount],
					specialPriceValue: [precio.specialPriceValue],
					specialPriceIsPercent: [precio.specialPriceIsPercent],
					posicion: [precio.posicion],
					preciosEspeciales: [precio.preciosEspeciales],
				});

				preciosArray.push(priceGroup);
			});
		}
	}

	private initializeModifierGroupsFormGroup(article: MenuItem): void {
		const gruposModificadoresArray = this.formGroup.get('gruposModificadores') as FormArray;
		gruposModificadoresArray.clear();

		if (article?.gruposModificadores) {
			(article?.gruposModificadores ?? []).forEach(grupo => {
				const groupModifier = this.formBuilder.group({
					posicion: [grupo.posicion],
					moduloDisponibles: [grupo.moduloDisponibles],
					id: [grupo.id],
					grupoModificador: this.formBuilder.group({
						idGrupomodificador: [grupo.grupoModificador.idGrupomodificador],
						nombre: [grupo.grupoModificador.nombre],
						descripcion: [grupo.grupoModificador.descripcion],
						minimo: [grupo.grupoModificador.minimo],
						maximo: [grupo.grupoModificador.maximo],
						gratis: [grupo.grupoModificador.gratis],
						usuario: [grupo.grupoModificador.usuario],
						mostrarVentanaModificador: [grupo.grupoModificador.mostrarVentanaModificador],
						detallesmodificadores: this.formBuilder.array(
							(grupo?.grupoModificador?.detallesmodificadores ?? []).map(detalle =>
								this.formBuilder.group({
									posicionModificador: [detalle.posicionModificador],
									precio: [detalle.precio],
									precioBase: [detalle.precioBase],
									precioInicial: [detalle.precio],
									idDetalleModificador: [detalle.idDetalleModificador],
									grupoModificadorId: [detalle.grupoModificadorId],
									addExtra: [!!detalle?.addExtra],
									noAdd: [!!detalle?.noAdd],
									isFree: [!!detalle?.isFree],
									cantidad: [detalle?.cantidad ?? 0],
									modificador: this.formBuilder.group({
										idModificador: [detalle.modificador.idModificador],
										nombre: [detalle.modificador.nombre],
										nombreCocina: [detalle.modificador.nombreCocina],
										nombreRecibo: [detalle.modificador.nombreRecibo],
										color: [detalle.modificador.color],
										usuario: [detalle.modificador.usuario],
									}),
									prefijosgrupomodificadores: this.formBuilder.array(
										detalle.prefijosgrupomodificadores.map(prefijo =>
											this.formBuilder.group({
												idPrefijoGrupoModificador: [prefijo.idPrefijoGrupoModificador],
												precio: [prefijo.precio],
												precioBase: [prefijo.precioBase],
												prefijoModificador: this.formBuilder.group({
													idPrefijoModificador: [prefijo.prefijoModificador.idPrefijoModificador],
													nombre: [prefijo.prefijoModificador.nombre],
													codigo: [prefijo.prefijoModificador.codigo],
													localId: [prefijo.prefijoModificador.localId],
												}),
											})
										)
									),
								})
							)
						),
					}),
				});
				gruposModificadoresArray.push(groupModifier);
			});
		}
	}

	private initializeTaxsFormGroup(article: MenuItem): void {
		const taxArray = this.formGroup.get('impuestos') as FormArray;
		taxArray.clear();

		if (article?.impuestos) {
			article?.impuestos.forEach((impuesto: Tax) => {
				const taxGroup = this.formBuilder.group({
					idImpuesto: [impuesto.idImpuesto],
					descripcion: [impuesto.descripcion],
					esPorcentaje: [impuesto.esPorcentaje],
					valor: [impuesto.valor],
					tipo: [impuesto.tipo],
					porDefecto: [impuesto.porDefecto],
					localId: [null],
				});

				taxArray.push(taxGroup);
			});
		}
	}

	private initializeSuggestItemsFormGroup(article: MenuItem): void {
		const suggestItemsArray = this.formGroup.get('suggestItems') as FormArray;
		suggestItemsArray.clear();

		if (article?.suggestItems) {
			article?.suggestItems.forEach((suggestItem: string) => {
				const suggestItemsGroup = this.formBuilder.control(suggestItem);

				suggestItemsArray.push(suggestItemsGroup);
			});
		}
	}

	override onDestroy(): void {
		super.onDestroy();
		this.reset();
	}

	private reset(): void {
		this.detailsWithQuantity = [];
		this.detailsWithQuantityNoAdd = [];
		this.store.dispatch(CART_RESET_FORM());
		this.store.dispatch(CART_FIND_BY_ID_SUCCESS({ cart: null }));
		this.store.dispatch(SUGGESTION_ITEMS_FIND_RESET());
	}

	getModifierGroupsAsFormGroups(): FormGroup[] {
		return ((this.formGroup.get('gruposModificadores') as FormArray)?.controls as FormGroup[]) || [];
	}

	isLongMode(): boolean {
		const prices = this.formGroup.get('productosCanal.precios') as FormArray | null;
		return homeProcessor.isSingleProductLongMode({
			modifierGroupCount: this.getModifierGroupsAsFormGroups().length,
			priceCount: prices?.length ?? 0,
		});
	}

	getDialogType(): DialogTypeEnum {
		return this.isLongMode() ? DialogTypeEnum.COMMON_LONG : DialogTypeEnum.COMMON_MEDIUM;
	}

	addToOrder(): void {
		this.store.dispatch(
			CART_PRODUCT_ORDER_PROCESS({
				formValues: this.formGroup.value,
				detailsWithQuantity: this.detailsWithQuantity,
				detailsWithQuantityNoAdd: this.detailsWithQuantityNoAdd,
				isEditing: this.view.isEditing,
			})
		);
	}

	isAddToOrderDisabled(): boolean {
		return homeProcessor.isSingleProductSubmissionDisabled({
			quantity: this.formGroup.get('cantidad')?.value,
			modifierGroups: this.getModifierGroupValues(),
		});
	}

	async closeModal(): Promise<void> {
		await this.modalCtrl.dismiss();
		this.reset();
	}

	private getModifierGroupValues(): Array<ModifierGroup | null> {
		return this.getModifierGroupsAsFormGroups().map(this.getModifierGroup);
	}

	async addSuggestionItem(article: MenuItem): Promise<void> {
		if (!this.isAddToOrderDisabled()) {
			this.addToOrder();
		}
		const topModal = await this.modalCtrl.getTop();
		if (topModal) {
			await topModal.dismiss();
		}
		this.store.dispatch(CART_FIND_BY_ID_SUCCESS({ cart: withDefaultSingleProductQuantity(article) }));

		await this.imageViewerService.openSingleProduct(article);
	}

	private getModifierGroup(modifierGroup: FormGroup): ModifierGroup | null {
		return modifierGroup.value?.grupoModificador ?? null;
	}
}
